import {
    ValueStore,
    initialGroupCallState,
    isActivePhase,
    ringModeForMemberCount,
    systemClock,
    type CallIntent,
    type Clock,
    type ExternalStore,
    type GroupCallController,
    type GroupCallPhase,
    type GroupCallState,
    type LocalMediaFailure,
    type LocalMediaKind,
    type NotifyMode,
    type Reaction,
    type ReceivedReaction,
    type ActiveRingInvite,
    type RingTargetState,
    type RoomCallSummary,
} from '@phonehq/rtc-core';
import logger from '../../../services/logger';
import {audioCaptureFor, videoCaptureFor, type MediaDevicePreferences} from '../platform/WebRtcPlatform';
import {NO_RTC_FEATURES, type RtcFeatureFlags} from './rtcFeatureFlags';
import {decideIncomingRing, type ParsedRtcNotification, type RtcNotificationEvent} from './incomingRing';
import type {RtcPowerLevelOutcome} from './rtcPowerLevels';
import {ringOutcomes, type RingOutcome} from './ringOutcomes';
import {SwitchableStore} from './switchableStore';

export const DEFAULT_RING_MEMBER_THRESHOLD = 10;
const MAX_HANDLED_NOTIFICATIONS = 200;

export interface IncomingGroupCall {
    readonly roomId: string;
    readonly notificationEventId: string;
    readonly intent: CallIntent;
    readonly callerUserId: string;
    readonly callerName: string;
    readonly roomName: string;
    readonly expiresAt: number;
    readonly ringId?: string;
}

export interface TargetedRing {
    readonly ringId: string;
    readonly roomId: string;
    readonly sender: string;
    readonly intent: CallIntent;
    readonly notificationEventId: string;
    readonly expiresAt: number;
}

export type RingMembersOutcome = 'ringing' | 'notified' | 'full' | 'not_joined' | 'cooldown' | 'failed';

export const RING_AGAIN_COOLDOWN_MS = 20_000;

export type GroupCallBlocker = 'disabled' | 'no_session' | 'not_owner' | 'busy';

export type GroupCallNotice =
    | GroupCallBlocker
    | 'power_levels_forbidden'
    | 'power_levels_failed';

export type CallBlockKind = 'no_e2ee' | 'other_device';

export type CallBlockOrigin = 'chat' | 'calls' | 'surface';

export interface CallBlock {
    readonly kind: CallBlockKind;
    readonly roomId: string;
    readonly origin: CallBlockOrigin;
}

export interface CallSwitch {
    readonly from: string;
    readonly to: string;
    readonly roomId: string;
    readonly intent: CallIntent;
    readonly expanded: boolean;
}

export interface GroupCallUiState {
    readonly preparing: boolean;
    readonly notice?: GroupCallNotice;
    readonly switching?: CallSwitch;
    readonly switchFailed?: CallSwitch;
}

export interface GroupCallJoinChoice {
    readonly intent: CallIntent;
    readonly microphoneEnabled: boolean;
    readonly cameraEnabled: boolean;
    readonly devices: MediaDevicePreferences;
    readonly targets?: readonly string[];
    readonly notify?: NotifyMode;
}

export interface GroupCallRoomDirectory {
    roomName(roomId: string): string;
    memberName(roomId: string, userId: string): string;
    isJoinedMember(roomId: string, userId: string): boolean;
    joinedMemberCount(roomId: string): number;
    latestCallNotification?(roomId: string, since?: number): string | undefined;
}

export interface GroupCallRingDirectory {
    targets(roomId: string): Promise<readonly RingTargetState[]>;
    activeInvites?(): Promise<readonly ActiveRingInvite[]>;
}

export interface GroupCallRuntime {
    readonly controller: GroupCallController;
    readonly roomCalls: ExternalStore<ReadonlyMap<string, RoomCallSummary>> & {dispose(): void};
    readonly localUserId: string;
    readonly rooms: GroupCallRoomDirectory;
    readonly ringDirectory?: GroupCallRingDirectory;
    ensurePowerLevels(roomId: string): Promise<RtcPowerLevelOutcome>;
    setDevicePreferences(devices: MediaDevicePreferences): void;
}

export interface GroupCallDialerPort {
    isOneToOneBusy(): boolean;
    setGroupCallActive(active: boolean): void;
}

export interface GroupCallRingerPort {
    start(): void;
    stop(): void;
}

export interface GroupCallNotifierPort {
    showRing(incoming: IncomingGroupCall): () => void;
    showCallStarted(details: {roomId: string; roomName: string; callerName: string; intent: CallIntent}): void;
    showBlocked(blocker: GroupCallBlocker): void;
    showMediaUnavailable(kinds: readonly LocalMediaKind[]): void;
}

export interface GroupCallSyncPort {
    pin(roomId: string): void;
    unpin(roomId: string): void;
}

export interface GroupCallOrchestratorDeps {
    readonly dialer: GroupCallDialerPort;
    readonly ringer: GroupCallRingerPort;
    readonly notifier: GroupCallNotifierPort;
    readonly sync: GroupCallSyncPort;
    readonly isTabOwner: () => boolean;
    readonly isDoNotDisturb?: () => boolean;
    readonly clock?: Clock;
    readonly ringMemberThreshold?: number;
    readonly supportsMediaEncryption?: () => boolean;
}

const RING_OUTCOME_BY_STOP_REASON: Readonly<Record<string, RingOutcome>> = {
    expired: 'missed',
    ended: 'missed',
    declined: 'declined',
};

const EMPTY_ROOM_CALLS: ReadonlyMap<string, RoomCallSummary> = new Map();

const isOccupied = (phase: GroupCallPhase): boolean => phase === 'lobby' || isActivePhase(phase);

export class GroupCallOrchestrator {
    readonly call = new SwitchableStore<GroupCallState>(initialGroupCallState);
    readonly roomCalls = new SwitchableStore<ReadonlyMap<string, RoomCallSummary>>(EMPTY_ROOM_CALLS);
    readonly incoming = new ValueStore<IncomingGroupCall | undefined>(undefined);
    readonly invite = new ValueStore<IncomingGroupCall | undefined>(undefined);
    readonly block = new ValueStore<CallBlock | undefined>(undefined);
    readonly ui = new ValueStore<GroupCallUiState>({preparing: false});
    readonly ringCooldowns = new ValueStore<ReadonlyMap<string, number>>(new Map());

    private readonly clock: Clock;
    private features: RtcFeatureFlags = NO_RTC_FEATURES;
    private runtime?: GroupCallRuntime;
    private runtimeDetachers: (() => void)[] = [];
    private lastPhase: GroupCallPhase = 'idle';
    private groupCallActive = false;
    private pinnedRoomId?: string;
    private ringTimer?: unknown;
    private inviteTimer?: unknown;
    private closeRingNotification?: () => void;
    private readonly handledNotifications = new Set<string>();
    private readonly waitingNotifications = new Map<string, {event: RtcNotificationEvent; timer: unknown}>();
    private readonly reactionListeners = new Set<(reaction: ReceivedReaction) => void>();
    private localReactionSequence = 0;
    private collectedMediaFailures?: LocalMediaKind[];
    private answered?: {readonly roomId: string; readonly notificationEventId: string};

    constructor(private readonly deps: GroupCallOrchestratorDeps) {
        this.clock = deps.clock ?? systemClock;
    }

    get controller(): GroupCallController | undefined {
        return this.runtime?.controller;
    }

    get localUserId(): string | undefined {
        return this.runtime?.localUserId;
    }

    getFeatures(): RtcFeatureFlags {
        return this.features;
    }

    setFeatures(features: RtcFeatureFlags): void {
        this.features = features;
        if (!features.groupCalls) {
            this.stopRinging('disabled');
            this.clearWaiting();
        }
    }

    attach(runtime: GroupCallRuntime | undefined): void {
        if (runtime === this.runtime) {
            return;
        }
        this.detachRuntime();
        this.runtime = runtime;
        this.call.use(runtime?.controller.store);
        this.roomCalls.use(runtime?.roomCalls);
        if (!runtime) {
            return;
        }
        this.runtimeDetachers.push(runtime.controller.store.subscribe(() => this.onCallState()));
        this.runtimeDetachers.push(runtime.roomCalls.subscribe(() => this.onRoomCallsChanged()));
        this.runtimeDetachers.push(runtime.controller.on('reaction', reaction => this.emitReaction(reaction)));
        this.runtimeDetachers.push(runtime.controller.on('mediaFailed', failure => this.onMediaFailed(failure)));
        this.runtimeDetachers.push(
            runtime.controller.on('ended', reason => {
                logger.info('Group call ended', {source: 'group_call', event: 'ended', reason, roomId: this.call.getSnapshot().roomId});
            }),
        );
        this.onCallState();
    }

    blocker(): GroupCallBlocker | undefined {
        if (!this.features.groupCalls) {
            return 'disabled';
        }
        if (!this.runtime) {
            return 'no_session';
        }
        if (!this.deps.isTabOwner()) {
            return 'not_owner';
        }
        if (isActivePhase(this.call.getSnapshot().phase) || this.deps.dialer.isOneToOneBusy()) {
            return 'busy';
        }
        return undefined;
    }

    isBusy(): boolean {
        return isOccupied(this.call.getSnapshot().phase) || this.deps.dialer.isOneToOneBusy();
    }

    async openLobby(roomId: string, intent: CallIntent, origin: CallBlockOrigin = 'surface'): Promise<boolean> {
        const blocker = this.blocker();
        const runtime = this.runtime;
        if (blocker || !runtime) {
            const reason = blocker ?? 'no_session';
            this.ui.set({preparing: false, notice: reason});
            this.deps.notifier.showBlocked(reason);
            logger.warn('Group call blocked', {source: 'group_call', event: 'start_blocked', reason, roomId});
            return false;
        }
        const block = this.callBlockFor(runtime, roomId);
        if (block) {
            this.block.set({kind: block, roomId, origin});
            logger.warn('Group call join blocked', {source: 'group_call', event: 'join_blocked', reason: block, roomId});
            return false;
        }
        this.block.set(undefined);
        if (this.incoming.getSnapshot()?.roomId === roomId) {
            this.stopRinging('answered');
        }
        this.ui.set({preparing: false});
        await runtime.controller.prepare(roomId, this.intentAllowed(intent));
        logger.info('Group call lobby opened', {
            source: 'group_call',
            event: 'lobby_opened',
            roomId,
            intent,
            existingParticipants: runtime.controller.store.getSnapshot().lobbyParticipants.length,
        });
        return true;
    }

    async join(choice: GroupCallJoinChoice): Promise<void> {
        const runtime = this.runtime;
        if (!runtime) {
            return;
        }
        const state = runtime.controller.store.getSnapshot();
        const roomId = state.roomId;
        if (state.phase !== 'lobby' || !roomId) {
            return;
        }
        this.ui.set({preparing: true});
        let outcome: RtcPowerLevelOutcome;
        try {
            outcome = await runtime.ensurePowerLevels(roomId);
        } catch (error) {
            logger.warn('Group call power levels could not be checked', {
                source: 'group_call',
                event: 'power_levels_failed',
                roomId,
                error: error instanceof Error ? error.message : String(error),
            });
            this.ui.set({preparing: false, notice: 'power_levels_failed'});
            return;
        }
        if (outcome === 'forbidden') {
            logger.warn('Group call needs a room admin to allow calls', {source: 'group_call', event: 'power_levels_forbidden', roomId});
            this.ui.set({preparing: false, notice: 'power_levels_forbidden'});
            return;
        }
        if (outcome === 'healed') {
            logger.info('Group call power levels repaired', {source: 'group_call', event: 'power_levels_healed', roomId});
        }
        if (runtime !== this.runtime || runtime.controller.store.getSnapshot().phase !== 'lobby') {
            this.ui.set({preparing: false});
            return;
        }
        this.ui.set({preparing: false});
        runtime.setDevicePreferences(choice.devices);
        const targets = choice.targets && choice.targets.length > 0 ? choice.targets : undefined;
        const notify = choice.notify ?? (targets ? 'notification' : this.notifyModeFor(runtime, roomId));
        const allowedIntent = this.intentAllowed(choice.intent);
        const allowedCamera = this.features.videoCalls && choice.cameraEnabled;
        logger.info('Group call join started', {
            source: 'group_call',
            event: 'join_started',
            roomId,
            intent: allowedIntent,
            notify,
            microphoneEnabled: choice.microphoneEnabled,
            cameraEnabled: allowedCamera,
        });
        const failures: LocalMediaKind[] = [];
        this.collectedMediaFailures = failures;
        try {
            await runtime.controller.join({
                intent: allowedIntent,
                notify,
                targets,
                microphoneEnabled: choice.microphoneEnabled,
                cameraEnabled: allowedCamera,
                audioCapture: audioCaptureFor(choice.devices.audioInputId),
                videoCapture: videoCaptureFor(choice.devices.videoInputId),
            });
        } finally {
            this.collectedMediaFailures = undefined;
        }
        if (failures.length > 0) {
            this.deps.notifier.showMediaUnavailable(failures);
        }
        if (this.answered?.roomId === roomId) {
            runtime.controller.adoptNotification(this.answered.notificationEventId);
        }
        this.answered = undefined;
        const outputId = choice.devices.audioOutputId;
        if (outputId && runtime.controller.store.getSnapshot().phase === 'joined') {
            await this.switchDevice('audiooutput', outputId);
        }
    }

    async switchDevice(kind: MediaDeviceKind, deviceId: string): Promise<boolean> {
        const controller = this.runtime?.controller;
        if (!controller) {
            return false;
        }
        try {
            return await controller.switchDevice(kind, deviceId);
        } catch (error) {
            logger.warn('Group call device switch failed', {
                source: 'group_call',
                event: 'device_switch_failed',
                kind,
                error: error instanceof Error ? error.message : String(error),
            });
            return false;
        }
    }

    async setMicrophone(enabled: boolean): Promise<boolean> {
        const controller = this.runtime?.controller;
        if (!controller) {
            return false;
        }
        try {
            await controller.setMicrophone(enabled);
            return true;
        } catch (error) {
            this.reportMediaFailure('microphone', error);
            return false;
        }
    }

    async setCamera(enabled: boolean): Promise<boolean> {
        const controller = this.runtime?.controller;
        if (!controller || (enabled && !this.features.videoCalls)) {
            return false;
        }
        try {
            await controller.setCamera(enabled);
            return true;
        } catch (error) {
            this.reportMediaFailure('camera', error);
            return false;
        }
    }

    onReaction(listener: (reaction: ReceivedReaction) => void): () => void {
        this.reactionListeners.add(listener);
        return () => {
            this.reactionListeners.delete(listener);
        };
    }

    async sendReaction(reaction: Reaction): Promise<boolean> {
        const controller = this.runtime?.controller;
        if (!controller) {
            return false;
        }
        const sent = await controller.sendReaction(reaction);
        if (sent) {
            this.localReactionSequence += 1;
            this.emitReaction({
                from: controller.room?.localParticipant?.identity || 'local',
                reaction,
                at: this.clock.now(),
                id: `local|${this.localReactionSequence}`,
            });
        }
        return sent;
    }

    async leave(): Promise<void> {
        await this.runtime?.controller.leave();
    }

    dismiss(): void {
        this.runtime?.controller.dismiss();
        this.ui.set({preparing: false});
    }

    dismissBlock(): void {
        this.block.set(undefined);
    }

    beginSwitch(switched: CallSwitch): void {
        this.ui.update(current => ({...current, preparing: false, switching: switched, switchFailed: undefined}));
    }

    finishSwitch(failed: boolean): void {
        this.ui.update(current => ({
            ...current,
            switching: undefined,
            switchFailed: failed ? current.switching : undefined,
        }));
    }

    async activeInvites(): Promise<readonly ActiveRingInvite[]> {
        const directory = this.runtime?.ringDirectory;
        return directory?.activeInvites ? directory.activeInvites() : [];
    }

    async declineInvite(): Promise<void> {
        const invite = this.invite.getSnapshot();
        const controller = this.runtime?.controller;
        if (!invite) {
            return;
        }
        this.clearInvite('declined');
        ringOutcomes.record(invite.notificationEventId, 'declined');
        if (!controller) {
            return;
        }
        try {
            await controller.declineIncoming(invite.roomId, invite.notificationEventId, invite.ringId);
        } catch (error) {
            logger.warn('Group call invite decline failed', {
                source: 'group_call',
                event: 'invite_decline_failed',
                roomId: invite.roomId,
                error: error instanceof Error ? error.message : String(error),
            });
        }
    }

    async acceptInvite(devices: MediaDevicePreferences = {}, expanded = false): Promise<void> {
        const invite = this.invite.getSnapshot();
        const runtime = this.runtime;
        if (!invite || !runtime) {
            return;
        }
        const current = this.call.getSnapshot();
        const switched: CallSwitch = {
            from: current.roomId ? runtime.rooms.roomName(current.roomId) : '',
            to: invite.roomName,
            roomId: invite.roomId,
            intent: invite.intent,
            expanded,
        };
        this.clearInvite('accepted');
        this.ui.set({preparing: false, switching: switched});
        logger.info('Group call invite accepted', {source: 'group_call', event: 'invite_accepted', roomId: invite.roomId, from: current.roomId});
        try {
            await this.leave();
            this.answered = {roomId: invite.roomId, notificationEventId: invite.notificationEventId};
            const opened = await this.openLobby(invite.roomId, invite.intent);
            if (opened) {
                await this.join({intent: invite.intent, microphoneEnabled: true, cameraEnabled: invite.intent === 'video', devices});
            }
        } catch (error) {
            logger.warn('Group call switch failed', {
                source: 'group_call',
                event: 'switch_failed',
                roomId: invite.roomId,
                error: error instanceof Error ? error.message : String(error),
            });
        }
        const phase = this.call.getSnapshot().phase;
        const joined = phase === 'joined' || phase === 'reconnecting';
        this.ui.update(currentUi => ({
            ...currentUi,
            preparing: false,
            switching: undefined,
            switchFailed: joined ? undefined : switched,
        }));
    }

    retrySwitch(devices: MediaDevicePreferences = {}): void {
        const failed = this.ui.getSnapshot().switchFailed;
        if (!failed) {
            return;
        }
        this.ui.update(current => ({...current, switchFailed: undefined}));
        this.runtime?.controller.dismiss();
        void this.openLobby(failed.roomId, failed.intent).then(opened => {
            if (opened) {
                return this.join({intent: failed.intent, microphoneEnabled: true, cameraEnabled: failed.intent === 'video', devices});
            }
            return undefined;
        });
    }

    dismissSwitchFailure(): void {
        this.ui.update(current => ({...current, switchFailed: undefined}));
        this.runtime?.controller.dismiss();
    }

    clearNotice(): void {
        this.ui.update(current => (current.notice ? {...current, notice: undefined} : current));
    }

    setMinimized(minimized: boolean): void {
        this.runtime?.controller.setMinimized(minimized);
    }

    async answerIncoming(intent: CallIntent, devices: MediaDevicePreferences = {}): Promise<void> {
        const incoming = this.incoming.getSnapshot();
        if (!incoming) {
            return;
        }
        this.stopRinging('answered');
        this.answered = {roomId: incoming.roomId, notificationEventId: incoming.notificationEventId};
        logger.info('Group call ring answered', {source: 'group_call', event: 'ring_answered', roomId: incoming.roomId, intent});
        const opened = await this.openLobby(incoming.roomId, intent);
        if (!opened) {
            return;
        }
        await this.join({intent, microphoneEnabled: true, cameraEnabled: intent === 'video', devices});
    }

    async declineIncoming(): Promise<void> {
        const incoming = this.incoming.getSnapshot();
        const controller = this.runtime?.controller;
        if (!incoming) {
            return;
        }
        this.stopRinging('declined');
        logger.info('Group call ring declined', {source: 'group_call', event: 'ring_declined', roomId: incoming.roomId});
        if (!controller) {
            return;
        }
        try {
            await controller.declineIncoming(incoming.roomId, incoming.notificationEventId, incoming.ringId);
        } catch (error) {
            logger.warn('Group call decline failed', {
                source: 'group_call',
                event: 'ring_decline_failed',
                roomId: incoming.roomId,
                error: error instanceof Error ? error.message : String(error),
            });
        }
    }

    handleTargetedRing(ring: TargetedRing): void {
        const now = this.clock.now();
        this.handleRtcNotification({
            eventId: ring.notificationEventId,
            roomId: ring.roomId,
            sender: ring.sender,
            originServerTs: now,
            content: {notification_type: 'ring', sender_ts: now, lifetime: Math.max(0, ring.expiresAt - now), 'm.call.intent': ring.intent},
            ringId: ring.ringId,
            dedupeKey: `ring:${ring.ringId}`,
            requireSenderInCall: true,
        });
    }

    async ringMembers(targets: readonly string[]): Promise<RingMembersOutcome> {
        const runtime = this.runtime;
        const state = this.call.getSnapshot();
        if (!runtime || !state.roomId || state.phase !== 'joined' || targets.length === 0) {
            return 'failed';
        }
        if (targets.every(target => this.ringCooldownLeft(target) > 0)) {
            return 'cooldown';
        }
        const fallback = state.notificationEventId ?? runtime.rooms.latestCallNotification?.(state.roomId, state.joinedAt);
        try {
            const result = await runtime.controller.ringMembers(targets, fallback);
            this.holdRing(targets, RING_AGAIN_COOLDOWN_MS);
            logger.info('Group call members rung', {source: 'group_call', event: 'members_rung', roomId: state.roomId, targets: targets.length, mode: result.mode});
            return result.mode === 'RING' ? 'ringing' : 'notified';
        } catch (error) {
            const rtcError = error as {code?: string; reason?: string; retryAfterMs?: number};
            logger.warn('Group call ring failed', {source: 'group_call', event: 'members_ring_failed', roomId: state.roomId, code: rtcError.code, reason: rtcError.reason});
            if (rtcError.code === 'call_full') {
                return 'full';
            }
            if (rtcError.code === 'rate_limited') {
                this.holdRing(targets, rtcError.retryAfterMs ?? RING_AGAIN_COOLDOWN_MS);
                return 'cooldown';
            }
            return rtcError.reason === 'target_not_joined' ? 'not_joined' : 'failed';
        }
    }

    ringCooldownLeft(target: string): number {
        return Math.max(0, (this.ringCooldowns.getSnapshot().get(target) ?? 0) - this.clock.now());
    }

    private holdRing(targets: readonly string[], ms: number): void {
        const now = this.clock.now();
        this.ringCooldowns.update(current => new Map([
            ...[...current].filter(([, until]) => until > now),
            ...targets.map(target => [target, now + ms] as const),
        ]));
    }

    async ringTargets(roomId: string): Promise<readonly RingTargetState[]> {
        const directory = this.runtime?.ringDirectory;
        return directory ? directory.targets(roomId) : [];
    }

    handleRtcNotification(event: RtcNotificationEvent): void {
        const runtime = this.runtime;
        const summary = runtime?.roomCalls.getSnapshot().get(event.roomId);
        const key = event.dedupeKey ?? event.eventId;
        const decision = decideIncomingRing(event, {
            now: this.clock.now(),
            localUserId: runtime?.localUserId ?? null,
            enabled: this.features.groupCalls && !!runtime && this.deps.isTabOwner(),
            busy: this.isBusy() || !!this.incoming.getSnapshot(),
            sessionActive: !!summary,
            localUserInCall: summary?.includesMe ?? false,
            senderInRoom: runtime?.rooms.isJoinedMember(event.roomId, event.sender) ?? false,
            senderInCall: summary?.userIds.includes(event.sender) ?? false,
            alreadyHandled: this.handledNotifications.has(key),
            doNotDisturb: this.deps.isDoNotDisturb?.() ?? false,
        });
        switch (decision.kind) {
            case 'ignore':
                if (decision.reason !== 'duplicate' && decision.reason !== 'own') {
                    logger.debug('Group call notification ignored', {
                        source: 'group_call',
                        event: 'notification_ignored',
                        roomId: event.roomId,
                        reason: decision.reason,
                    });
                }
                return;
            case 'wait':
                this.waitFor(event, decision.notification);
                return;
            case 'ring':
                this.markHandled(key);
                this.startRinging(event, decision.notification);
                return;
            case 'notify':
                this.markHandled(key);
                if (decision.downgradedFromRing && this.invitesWhileInCall(event.roomId)) {
                    this.showInvite(event, decision.notification);
                    return;
                }
                this.notifyStarted(event, decision.notification, decision.downgradedFromRing);
                return;
        }
    }

    dispose(): void {
        this.detachRuntime();
        this.runtime = undefined;
        this.call.use(undefined);
        this.roomCalls.use(undefined);
    }

    private onMediaFailed(failure: LocalMediaFailure): void {
        logger.warn('Group call device unavailable', {
            source: 'group_call',
            event: 'media_unavailable',
            kind: failure.kind,
            roomId: this.call.getSnapshot().roomId,
            error: failure.error.message,
        });
        if (this.collectedMediaFailures) {
            this.collectedMediaFailures.push(failure.kind);
        } else {
            this.deps.notifier.showMediaUnavailable([failure.kind]);
        }
    }

    private reportMediaFailure(kind: LocalMediaKind, error: unknown): void {
        logger.warn('Group call device unavailable', {
            source: 'group_call',
            event: 'media_unavailable',
            kind,
            roomId: this.call.getSnapshot().roomId,
            error: error instanceof Error ? error.message : String(error),
        });
        this.deps.notifier.showMediaUnavailable([kind]);
    }

    private callBlockFor(runtime: GroupCallRuntime, roomId: string): CallBlockKind | undefined {
        if (!(this.deps.supportsMediaEncryption?.() ?? true)) {
            return 'no_e2ee';
        }
        const summary = runtime.roomCalls.getSnapshot().get(roomId);
        return summary?.onOtherDevice ? 'other_device' : undefined;
    }

    private invitesWhileInCall(roomId: string): boolean {
        const state = this.call.getSnapshot();
        const inCall = state.phase === 'joined' || state.phase === 'reconnecting';
        return inCall && state.roomId !== roomId && !(this.deps.isDoNotDisturb?.() ?? false);
    }

    private showInvite(event: RtcNotificationEvent, notification: ParsedRtcNotification): void {
        if (this.inviteTimer !== undefined) {
            this.clock.clearTimeout(this.inviteTimer);
        }
        const invite = this.describe(event, notification);
        this.invite.set(invite);
        this.inviteTimer = this.clock.setTimeout(
            () => this.clearInvite('expired'),
            Math.max(0, notification.expiresAt - this.clock.now()),
        );
        logger.info('Group call invite shown', {source: 'group_call', event: 'invite_shown', roomId: invite.roomId, intent: invite.intent});
    }

    private clearInvite(reason: string): void {
        const invite = this.invite.getSnapshot();
        if (this.inviteTimer !== undefined) {
            this.clock.clearTimeout(this.inviteTimer);
            this.inviteTimer = undefined;
        }
        if (!invite) {
            return;
        }
        this.invite.set(undefined);
        if (reason === 'expired' || reason === 'ended') {
            ringOutcomes.record(invite.notificationEventId, 'missed');
        }
        logger.info('Group call invite cleared', {source: 'group_call', event: 'invite_cleared', roomId: invite.roomId, reason});
    }

    private intentAllowed(intent: CallIntent): CallIntent {
        return this.features.videoCalls ? intent : 'audio';
    }

    private emitReaction(reaction: ReceivedReaction): void {
        [...this.reactionListeners].forEach(listener => listener(reaction));
    }

    private notifyModeFor(runtime: GroupCallRuntime, roomId: string): NotifyMode {
        const summary = runtime.roomCalls.getSnapshot().get(roomId);
        if (summary && summary.participantCount > 0) {
            return 'none';
        }
        return ringModeForMemberCount(
            runtime.rooms.joinedMemberCount(roomId),
            this.deps.ringMemberThreshold ?? DEFAULT_RING_MEMBER_THRESHOLD,
        );
    }

    private onCallState(): void {
        const state = this.call.getSnapshot();
        const previous = this.lastPhase;
        this.lastPhase = state.phase;
        this.setGroupCallActive(isOccupied(state.phase));
        this.updatePinnedRoom(isActivePhase(state.phase) ? state.roomId : undefined);
        if (previous === state.phase) {
            return;
        }
        if (isOccupied(state.phase) && this.incoming.getSnapshot()) {
            this.stopRinging('busy');
        }
        if (!isActivePhase(state.phase) && this.invite.getSnapshot() && !this.ui.getSnapshot().switching) {
            this.clearInvite('call_left');
        }
        if (state.phase === 'joined' && previous === 'joining') {
            this.recordAnswered(state.roomId);
            logger.info('Group call joined', {
                source: 'group_call',
                event: 'joined',
                roomId: state.roomId,
                intent: state.intent,
                participantCount: state.participants.length,
            });
        } else if (state.phase === 'reconnecting') {
            logger.warn('Group call reconnecting', {source: 'group_call', event: 'reconnecting', roomId: state.roomId});
        } else if (state.phase === 'failed') {
            logger.error('Group call failed', {
                source: 'group_call',
                event: 'failed',
                roomId: state.roomId,
                code: state.error?.code,
            });
        }
    }

    private recordAnswered(roomId: string | undefined): void {
        const runtime = this.runtime;
        if (!roomId || !runtime?.rooms.latestCallNotification) {
            return;
        }
        const notificationId = runtime.rooms.latestCallNotification(roomId, runtime.roomCalls.getSnapshot().get(roomId)?.startedAt);
        if (notificationId) {
            ringOutcomes.record(notificationId, 'answered');
        }
    }

    private setGroupCallActive(active: boolean): void {
        if (active === this.groupCallActive) {
            return;
        }
        this.groupCallActive = active;
        this.deps.dialer.setGroupCallActive(active);
    }

    private updatePinnedRoom(roomId: string | undefined): void {
        if (roomId === this.pinnedRoomId) {
            return;
        }
        if (this.pinnedRoomId) {
            this.deps.sync.unpin(this.pinnedRoomId);
        }
        this.pinnedRoomId = roomId;
        if (roomId) {
            this.deps.sync.pin(roomId);
        }
    }

    private onRoomCallsChanged(): void {
        const calls = this.roomCalls.getSnapshot();
        const invite = this.invite.getSnapshot();
        if (invite) {
            const summary = calls.get(invite.roomId);
            if (!summary) {
                this.clearInvite('ended');
            } else if (summary.includesMe) {
                this.clearInvite('answered_elsewhere');
            }
        }
        const incoming = this.incoming.getSnapshot();
        if (incoming) {
            const summary = calls.get(incoming.roomId);
            if (!summary) {
                this.stopRinging('ended');
            } else if (summary.includesMe) {
                this.stopRinging('answered_elsewhere');
            }
        }
        [...this.waitingNotifications.entries()].forEach(([eventId, waiting]) => {
            if (calls.has(waiting.event.roomId)) {
                this.clock.clearTimeout(waiting.timer);
                this.waitingNotifications.delete(eventId);
                this.handleRtcNotification(waiting.event);
            }
        });
    }

    private waitFor(event: RtcNotificationEvent, notification: ParsedRtcNotification): void {
        const key = event.dedupeKey ?? event.eventId;
        if (this.waitingNotifications.has(key)) {
            return;
        }
        const timer = this.clock.setTimeout(
            () => this.waitingNotifications.delete(key),
            Math.max(0, notification.expiresAt - this.clock.now()),
        );
        this.waitingNotifications.set(key, {event, timer});
    }

    private clearWaiting(): void {
        this.waitingNotifications.forEach(waiting => this.clock.clearTimeout(waiting.timer));
        this.waitingNotifications.clear();
    }

    private markHandled(eventId: string): void {
        this.handledNotifications.add(eventId);
        if (this.handledNotifications.size > MAX_HANDLED_NOTIFICATIONS) {
            const oldest = this.handledNotifications.values().next().value;
            if (oldest !== undefined) {
                this.handledNotifications.delete(oldest);
            }
        }
    }

    private describe(event: RtcNotificationEvent, notification: ParsedRtcNotification): IncomingGroupCall {
        const rooms = this.runtime?.rooms;
        return {
            roomId: event.roomId,
            notificationEventId: event.eventId,
            intent: notification.intent,
            callerUserId: event.sender,
            callerName: rooms?.memberName(event.roomId, event.sender) ?? event.sender,
            roomName: rooms?.roomName(event.roomId) ?? event.roomId,
            expiresAt: notification.expiresAt,
            ...(event.ringId ? {ringId: event.ringId} : {}),
        };
    }

    private startRinging(event: RtcNotificationEvent, notification: ParsedRtcNotification): void {
        const incoming = this.describe(event, notification);
        this.incoming.set(incoming);
        this.deps.ringer.start();
        this.closeRingNotification = this.deps.notifier.showRing(incoming);
        this.ringTimer = this.clock.setTimeout(
            () => this.stopRinging('expired'),
            Math.max(0, notification.expiresAt - this.clock.now()),
        );
        logger.info('Group call ringing', {
            source: 'group_call',
            event: 'ring_received',
            roomId: incoming.roomId,
            intent: incoming.intent,
        });
    }

    private notifyStarted(event: RtcNotificationEvent, notification: ParsedRtcNotification, downgraded: boolean): void {
        const details = this.describe(event, notification);
        this.deps.notifier.showCallStarted({
            roomId: details.roomId,
            roomName: details.roomName,
            callerName: details.callerName,
            intent: details.intent,
        });
        logger.info('Group call started notification', {
            source: 'group_call',
            event: 'notification_received',
            roomId: details.roomId,
            intent: details.intent,
            downgradedFromRing: downgraded,
        });
    }

    private stopRinging(reason: string): void {
        const incoming = this.incoming.getSnapshot();
        if (!incoming) {
            return;
        }
        if (this.ringTimer !== undefined) {
            this.clock.clearTimeout(this.ringTimer);
            this.ringTimer = undefined;
        }
        this.deps.ringer.stop();
        this.closeRingNotification?.();
        this.closeRingNotification = undefined;
        this.incoming.set(undefined);
        const outcome = RING_OUTCOME_BY_STOP_REASON[reason];
        if (outcome) {
            ringOutcomes.record(incoming.notificationEventId, outcome);
        }
        logger.info('Group call ring stopped', {source: 'group_call', event: 'ring_stopped', roomId: incoming.roomId, reason});
    }

    private detachRuntime(): void {
        const runtime = this.runtime;
        this.runtimeDetachers.forEach(detach => detach());
        this.runtimeDetachers = [];
        this.stopRinging('session_closed');
        this.clearInvite('session_closed');
        this.block.set(undefined);
        this.clearWaiting();
        this.updatePinnedRoom(undefined);
        this.setGroupCallActive(false);
        this.lastPhase = 'idle';
        if (runtime) {
            runtime.controller.dispose();
            runtime.roomCalls.dispose();
        }
    }
}
